<template>
  <div
    v-if="isVisible"
    class="modal fade show d-block"
    tabindex="-1"
    style="background-color: rgba(0, 0, 0, 0.5)"
    @click.self="close"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-file-invoice me-2" aria-hidden="true"></i>
            {{ $t("notaComercial.noteForm.modalTitle") }}
          </h5>
          <button type="button" class="btn-close" :aria-label="$t('close')" @click="close"></button>
        </div>
        <div class="modal-body">
          <NotaComercialNoteForm
            v-model="form"
            :lists="effectiveLists"
            :disabled="saving"
            :lock-contact="lockContact"
            :lock-assignment="lockAssignment"
            :lock-shipping="lockShipping"
            :lock-proform="lockProform"
            :assignment-hint="assignmentHint"
            :contacts="contacts"
          />
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="close">
            {{ $t("cancel") }}
          </button>
          <button type="button" class="btn btn-primary" :disabled="saving" @click="submit">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            {{ saving ? $t("financialSummary.fobNotaComercial.saving") : $t("financialSummary.fobNotaComercial.save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";
import { escapeHtml } from "@/utils/reportEditPrompt";
import NotaComercialNoteForm from "./NotaComercialNoteForm.vue";
import {
  buildNotaComercialCreatePayload,
  buildNotaComercialFormPrefill,
  contactIdForNoteKind,
  createDefaultNotaComercialForm,
  getReferenceId,
  defaultNotaComercialCurrencyId,
  remainingAppliedToAmount,
  syncSingleAppliedToLineAmount,
  validateNotaComercialForm,
  noteMissingReceivedFile,
} from "../utils/notaComercialNoteFormUtils";
import {
  creditNoteShippingSaveErrorKeys,
  promptNotaComercialMissingFile,
} from "../utils/notaComercialSaveErrors";

export default {
  name: "NotaComercialNoteModal",
  components: {
    NotaComercialNoteForm,
  },
  props: {
    lists: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isVisible: false,
      saving: false,
      form: createDefaultNotaComercialForm(),
      extraLists: [],
      /** null = decide el propio popup según la variante; false = siempre editable. */
      lockContactRequested: null,
      lockAssignment: false,
      lockShipping: false,
      lockProform: false,
      assignmentHint: "",
      namePrefix: "NC",
      contacts: {},
    };
  },
  computed: {
    effectiveLists() {
      if (Array.isArray(this.lists) && this.lists.length) return this.lists;
      return this.extraLists;
    },
    /**
     * El contacto se traba solo si la operación sabe cuál va con la variante elegida.
     * Al pasar a una variante de proveedor sin proveedor conocido queda editable, en vez
     * de quedar trabado con el cliente (que era el bug de las notas de proveedor).
     */
    lockContact() {
      if (this.lockContactRequested === false) return false;
      return !!contactIdForNoteKind(this.form.noteKind, this.contacts);
    },
  },
  methods: {
    async ensureLists() {
      if (this.currencyOptionsFromLists(this.effectiveLists).length) return;
      try {
        const { data } = await wencesApi.get("/api/gotrading/lists");
        this.extraLists = Array.isArray(data) ? data : [];
      } catch {
        this.extraLists = [];
      }
    },
    currencyOptionsFromLists(listDocs) {
      const list = (listDocs || []).find((l) => l && l.type === "currency");
      if (!list || !Array.isArray(list.values)) return [];
      return list.values.filter((v) => v && v._id);
    },
    defaultCurrencyId() {
      return defaultNotaComercialCurrencyId(this.effectiveLists);
    },
    buildAssignmentHint(context = {}) {
      const modelName = String(context.sourceModel || context.modelName || "").toLowerCase();
      const itemData = context.itemData || null;
      const explicit = context.appliedTo;
      if (explicit && explicit.id) {
        const name = (explicit.name && String(explicit.name).trim()) || String(explicit.id);
        return explicit.type === "proform"
          ? this.$t("financialSummary.fobNotaComercial.appliedToProformHint", { name })
          : this.$t("financialSummary.fobNotaComercial.appliedToShippingHint", { name });
      }
      if (modelName === "proform" && itemData) {
        const name = (itemData.name && String(itemData.name).trim()) || itemData._id || "—";
        return this.$t("financialSummary.fobNotaComercial.appliedToProformHint", { name });
      }
      if (modelName === "shipping" && itemData) {
        const name = (itemData.name && String(itemData.name).trim()) || itemData._id || "—";
        return this.$t("financialSummary.fobNotaComercial.appliedToShippingHint", { name });
      }
      return "";
    },
    async open(context = {}) {
      await this.ensureLists();
      this.contacts = {
        clientId: context.clientId ? String(context.clientId) : "",
        supplierId: context.supplierId ? String(context.supplierId) : "",
      };
      this.form = buildNotaComercialFormPrefill(context);
      if (!this.form.currencyId) {
        this.form.currencyId = this.defaultCurrencyId();
      }
      const modelName = String(context.sourceModel || context.modelName || "").toLowerCase();
      const explicitTarget = context.appliedTo && context.appliedTo.id ? context.appliedTo.type : "";
      this.lockContactRequested = context.lockContact === false ? false : null;
      this.lockAssignment =
        !!explicitTarget || modelName === "proform" || modelName === "shipping";
      this.lockShipping = explicitTarget ? explicitTarget === "shipping" : modelName === "shipping";
      this.lockProform = explicitTarget ? explicitTarget === "proform" : modelName === "proform";
      this.assignmentHint = this.buildAssignmentHint(context);
      this.namePrefix = context.namePrefix || "NC";
      this.isVisible = true;
    },
    close() {
      if (this.saving) return;
      this.isVisible = false;
    },
    /**
     * La nota se abrió desde un gasto que ya está pago: se dice para quién era y, si el
     * usuario quería otro contacto, dónde se carga una nota suelta.
     */
    async showAppliedNothingPending(form) {
      const contactName = await this.fetchContactName(getReferenceId(form.contactRef));
      const intro = contactName
        ? this.$tn("notaComercial.noteForm.appliedNothingPendingFor", { contact: contactName })
        : this.$tn("notaComercial.noteForm.validationAppliedNothingPending");
      const simulatorPath = "/nota-comercial/simulador";
      const html =
        `<p>${escapeHtml(intro)}</p>` +
        `<p class="mb-0">${escapeHtml(this.$tn("notaComercial.noteForm.appliedNothingPendingOtherContact"))} ` +
        `<a href="${simulatorPath}" data-simulator-link>${escapeHtml(
          this.$tn("notaComercial.noteForm.appliedNothingPendingSimulatorLink")
        )}</a>.</p>`;
      await Swal.fire({
        icon: "warning",
        title: this.$tn("notaComercial.noteForm.appliedNothingPendingTitle"),
        html,
        didOpen: (popup) => {
          const link = popup.querySelector("[data-simulator-link]");
          if (!link) return;
          link.addEventListener("click", (event) => {
            event.preventDefault();
            Swal.close();
            this.isVisible = false;
            this.$router.push(simulatorPath);
          });
        },
      });
    },
    async fetchContactName(contactId) {
      if (!contactId) return "";
      try {
        const { data } = await wencesApi.get(`/api/gotrading/dynamic/contact/${contactId}`);
        return (data && data.name) || "";
      } catch (e) {
        return "";
      }
    },
    async submit() {
      const syncedForm = syncSingleAppliedToLineAmount(this.form);
      this.form = syncedForm;
      const validationError = validateNotaComercialForm(syncedForm, (key) => this.$t(key));
      if (validationError === this.$t("notaComercial.noteForm.validationAppliedNothingPending")) {
        await this.showAppliedNothingPending(syncedForm);
        return;
      }
      if (validationError) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("notaComercial.noteForm.validationTitle"),
          text: validationError,
        });
        return;
      }
      // Las notas que nos dan llegan como documento: sin el archivo quedan sin respaldo.
      // El backend valida lo mismo, así que al aceptar hay que decírselo con forceSave.
      let forceSaveWithoutFile = false;
      if (noteMissingReceivedFile(syncedForm)) {
        const saveAnyway = await promptNotaComercialMissingFile((key) => this.$t(key));
        if (!saveAnyway) return;
        forceSaveWithoutFile = true;
      }
      this.saving = true;
      try {
        const remainingBeforeSave = remainingAppliedToAmount(syncedForm);
        const payload = buildNotaComercialCreatePayload(syncedForm, {
          namePrefix: this.namePrefix,
          lockShipping: this.lockShipping,
          lockProform: this.lockProform,
        });
        const { data: createdNote } = await wencesApi.post(
          "/api/gotrading/dynamic/notacomercial",
          forceSaveWithoutFile ? { ...payload, forceSave: true } : payload
        );
        this.isVisible = false;
        const remainingHint =
          remainingBeforeSave > 0.01
            ? this.$t("notaComercial.noteForm.remainingCreditAlert", {
                amount: remainingBeforeSave.toFixed(2),
              })
            : "";
        await Swal.fire({
          icon: remainingHint ? "info" : "success",
          title: this.$tn("financialSummary.fobNotaComercial.successTitle"),
          html: remainingHint
            ? `${this.$tn("financialSummary.fobNotaComercial.successText")}<br/><br/>${remainingHint}`
            : this.$tn("financialSummary.fobNotaComercial.successText"),
        });
        // La nota creada va en el evento: quien abrió el popup puede necesitar su id
        // (la línea de gastos la cuelga de su campo commercialnotes).
        this.$emit("saved", createdNote || null);
      } catch (error) {
        const known = creditNoteShippingSaveErrorKeys(error);
        const titleKey = known ? known.title : "financialSummary.fobNotaComercial.errorTitle";
        const text = known
          ? this.$t(known.text)
          : error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            this.$t("financialSummary.fobNotaComercial.errorTitle");
        await Swal.fire({
          icon: known ? "warning" : "error",
          title: this.$tn(titleKey),
          text,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
